import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Country } from "country-state-city";

interface SearchFiltersProps {
  onFilterChange: (filters: any) => void;
  filters: any;
  isMapView: boolean;
  setIsMapView: (isMapView: boolean) => void;
}

export default function SearchFilters({ onFilterChange, filters, isMapView, setIsMapView }: SearchFiltersProps) {
  const [sortBy, setSortBy] = useState("date-desc");  
  const country = Country.getAllCountries()

  const handleSortChange = (value: string) => {
    setSortBy(value);
    onFilterChange({ ...filters, sortBy: value });
  };

  const toggleMapView = () => {
    setIsMapView(!isMapView);
    // Map view toggle functionality would be implemented here
  };

  return (
    <div className="grid grid-cols-2 w-full md:w-auto gap-2 justify-between  md:flex md:items-center md:space-x-4">
      {/* View Toggle */}
      <div className="flex bg-gray-100 rounded-lg p-1 ">
        <button
          className={`px-3 py-2 rounded-md shadow-sm transition-colors w-full md:w-auto ${!isMapView ? 'bg-white text-primary' : 'text-gray-600 hover:text-gray-900'
            }`}
          onClick={() => setIsMapView(false)}
          data-testid="button-grid-view"
        >
          <i className="fas fa-th-large mr-1"></i>Grid
        </button>
        <button
          className={`px-3 py-2 rounded-md transition-colors w-full md:w-auto ${isMapView ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          onClick={toggleMapView}
          data-testid="button-map-view"
        >
          <i className="fas fa-map mr-1"></i>Map
        </button>
      </div>

      {/* Sort Dropdown */}
      <Select value={sortBy} onValueChange={handleSortChange}>
        <SelectTrigger className="w-auto md:w-48 h-12 md:h-auto ml-5 md:ml-0" data-testid="select-sort-by">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="date-desc">Most Recent</SelectItem>
          <SelectItem value="date-asc">Oldest First</SelectItem>
          <SelectItem value="price-asc">Price: Low to High</SelectItem>
          <SelectItem value="price-desc">Price: High to Low</SelectItem>
        </SelectContent>
      </Select>

      {/* Advanced Filters */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" data-testid="button-advanced-filters">
            <i className="fas fa-sliders-h mr-2"></i>Filters
          </Button>

        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">Advanced Filters</h4>
              {Object.keys(filters).length > 0 && <Button variant="error" onClick={() => onFilterChange({})}>
                <i className="fas fa-eraser mr-2"></i>Clear
              </Button>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                <Select value={filters?.propertyType} onValueChange={(value) => onFilterChange({ ...filters, propertyType: value === "all" ? "" : value })}>
                  <SelectTrigger data-testid="select-filter-property-type">
                    <SelectValue placeholder="Any Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Type</SelectItem>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                <Select value={filters?.beds?.toString()} onValueChange={(value) => onFilterChange({ ...filters, beds: value === "all" ? undefined : Number(value) })}>
                  <SelectTrigger data-testid="select-filter-bedrooms">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="1">1+</SelectItem>
                    <SelectItem value="2">2+</SelectItem>
                    <SelectItem value="3">3+</SelectItem>
                    <SelectItem value="4">4+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Price (€)</label>
                <Select value={filters?.minPrice?.toString()} onValueChange={(value) => onFilterChange({ ...filters, minPrice: value === "0" ? undefined : Number(value) })}>
                  <SelectTrigger data-testid="select-filter-min-price">
                    <SelectValue placeholder="No Min" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">No Min</SelectItem>
                    <SelectItem value="100000">₹100,000</SelectItem>
                    <SelectItem value="200000">₹200,000</SelectItem>
                    <SelectItem value="300000">₹300,000</SelectItem>
                    <SelectItem value="400000">₹400,000</SelectItem>
                    <SelectItem value="500000">₹500,000</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Price (€)</label>
                <Select value={filters?.maxPrice?.toString()} onValueChange={(value) => onFilterChange({ ...filters, maxPrice: value === "999999999" ? undefined : Number(value) })}>
                  <SelectTrigger data-testid="select-filter-max-price">
                    <SelectValue placeholder="No Max" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="999999999">No Max</SelectItem>
                    <SelectItem value="300000">₹300,000</SelectItem>
                    <SelectItem value="400000">₹400,000</SelectItem>
                    <SelectItem value="500000">₹500,000</SelectItem>
                    <SelectItem value="600000">₹600,000</SelectItem>
                    <SelectItem value="800000">₹800,000</SelectItem>
                    <SelectItem value="1000000">₹1,000,000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <Select value={filters?.country?.toString()} onValueChange={(value) => onFilterChange({ ...filters, country: value === "all" ? "" : value })}>
                <SelectTrigger data-testid="select-filter-county">
                  <SelectValue placeholder="All Counties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Counties</SelectItem>
                  {
                    country?.map((country : any) => (
                      <SelectItem key={country.isoCode} value={country.name}>
                        {country.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
